import React, { useState } from 'react'
import { csx } from '@vtex/admin-ui'
import { graphql } from 'babel-plugin-relay/macro'
import { useLazyLoadQuery } from 'react-relay-offline'
import { clearSavedAttachments } from '@/checkout-instore-legacy/cart/actions'
import { getReduxStore } from '@/checkout-instore-legacy/redux/reduxStore'

import { useHistory, useQueryParams } from '../../../../core/router'
import type { AssemblyItemsQuery } from './__generated__/AssemblyItemsQuery.graphql'
import { useAssemblyItems } from './hooks/useAssemblyItems'
import { AssemblyItemsTopbar } from './AssemblyItemsTopbar'
import { AssemblyItemsList } from './AssemblyItemsList'
import { getEncodedSkuId } from './utils/getEncodedSkuId'

export const AssemblyItems = () => {
  const ASSEMBLY_ITEMS_DATA = graphql`
    query AssemblyItemsQuery($skuId: ID!) {
      productSku(id: $skuId) {
        assemblyItems {
          edges {
            node {
              id
              required
              ...AssemblyItemsList_ProductSkuAssemblyItem
            }
          }
        }
      }
    }
  `

  const [currentAssemblyItemIndex, setCurrentAssemblyItemIndex] = useState(0)

  const skuId = useQueryParams().get('skuId')

  const {
    data: assemblyItemsData,
    error: assemblyItemsError,
  } = useLazyLoadQuery<AssemblyItemsQuery>(
    ASSEMBLY_ITEMS_DATA,
    {
      skuId: getEncodedSkuId() ?? '',
    },
    {
      fetchPolicy: 'store-and-network',
      skip: !skuId,
    }
  )

  const { handleAddAssemblyItemToCart, isBusy, isMobile } = useAssemblyItems(
    skuId
  )

  const assemblyItems = assemblyItemsData?.productSku?.assemblyItems.edges

  const currentAssemblyItem = assemblyItems?.[currentAssemblyItemIndex]
    ? assemblyItems[currentAssemblyItemIndex].node
    : null

  const hasMoreOneAssemblyItem =
    assemblyItems && currentAssemblyItemIndex < assemblyItems.length - 1

  const history = useHistory()

  const handleNextAssemblyItem = () => {
    if (assemblyItems && currentAssemblyItemIndex < assemblyItems.length - 1) {
      setCurrentAssemblyItemIndex(currentAssemblyItemIndex + 1)
    }
  }

  const handlePreviousAssemblyItem = () => {
    if (currentAssemblyItemIndex > 0) {
      setCurrentAssemblyItemIndex(currentAssemblyItemIndex - 1)
    } else {
      getReduxStore()?.dispatch(clearSavedAttachments())

      history.goBack()
    }
  }

  if (!assemblyItemsData) {
    return null
  }

  if (assemblyItemsError) {
    throw assemblyItemsError
  }

  return (
    <>
      <AssemblyItemsTopbar onClick={handlePreviousAssemblyItem} />
      <div
        className={csx({
          paddingY: '$space-10',
          paddingX: isMobile ? '$space-5' : '13rem',
          position: 'relative',
        })}
      >
        {currentAssemblyItem && (
          <AssemblyItemsList
            key={currentAssemblyItem.id}
            productSkuAssemblyItem={currentAssemblyItem}
            handleAddAssemblyItemToCart={handleAddAssemblyItemToCart}
            isBusy={isBusy}
            hasMoreOneAssemblyItem={hasMoreOneAssemblyItem}
            handleNextAssemblyItem={handleNextAssemblyItem}
            required={currentAssemblyItem.required}
          />
        )}
      </div>
    </>
  )
}
